import { defineComponent as _defineComponent } from 'vue'
import { apiPost } from '@/store/api';
import { usePortcallStore } from '@/store/portcallStore';
import { computed, ref, watch } from 'vue';

interface Props {
  value: boolean;
  /** The portcall ID */
  callId: string;
  /** The selected contact ID's (from the clients list) */
  selectedContactIds: number[];
  /** The selected clients ID */
  clientId: number;
  /** The hotlist ID. */
  hotlistId: number;
}

interface IHotlistContact {
  HCO_ID: number;
  HCO_HOTLIST_ID: number;
  HCO_CONTACT_ID: number | null;
  HCO_COM_TYPE: number;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'SelectHotlistRecipientsDialog',
  props: {
    value: { type: Boolean },
    callId: null,
    selectedContactIds: null,
    clientId: null,
    hotlistId: null
  },
  emits: ["input", "recipients-updated"],
  setup(__props: any, { emit }: { emit: ({
  /** Emitted when the dialog closes. */
  (e: 'input', payloadProperty: boolean): void;
  /** Emitted when the recipients have been updated. */
  (e: 'recipients-updated'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  value: boolean;
  /** The portcall ID */
  callId: string;
  /** The selected contact ID's (from the clients list) */
  selectedContactIds: number[];
  /** The selected clients ID */
  clientId: number;
  /** The hotlist ID. */
  hotlistId: number;
};



const portcallStore = usePortcallStore();



const isLoading = ref(false);
const isSaving = ref(false);

const selectedKeys = ref<any[]>([]);

function rowClass(item: any) {
  let result = '';
  if (selectedKeys.value.includes(item.ID)) {
    result += 'font-weight-bold';
  }
  if (item.INACTIVE) {
    result += ' text-decoration-line-through';
  }
  return result;
}

const contacts = ref<any[]>([]);

const companyContact = computed(() => {
  const client = portcallStore.hotlist.find((item: any) => item.CLIENT_ID === props.clientId);
  if (!client) {
    return undefined;
  }
  return {
    ID: client.CLIENT_ID,
    NAME: 'Company main address/number',
    TITLE: null,
    DEPARTMENT: null,
    EMAIL: client.C_EMAIL,
    PHONE_PRIVATE: client.C_PHONE,
    PHONE_MOBILE: null,
    FULL_NAME: 'Company',
  };
});

async function getContacts() {
  if (props.clientId) {
    isLoading.value = true;
    const result = await apiPost('contacts', { clientId: props.clientId, id: -1, category: -1, searchStr: '' });
    if (Array.isArray(result)) {
      contacts.value = result.sort((a, b) => {
        if (a.LAST_NAME > b.LAST_NAME) {
          return 1;
        }
        if (a.LAST_NAME < b.LAST_NAME) {
          return -1;
        }
        if (a.FIRST_NAME > b.FIRST_NAME) {
          return 1;
        }
        if (a.FIRST_NAME < b.FIRST_NAME) {
          return -1;
        }
        return 0;
      });
    }
    if (Array.isArray(props.selectedContactIds) && props.selectedContactIds.length > 0) {
      selectedKeys.value = [...props.selectedContactIds];
    }
    isLoading.value = false;
  }
}

const modelValue = computed({
  get: () => props.value,
  set: (val) => {
    emit('input', val);
  },
});

watch(
  () => modelValue.value,
  (val) => {
    if (val) {
      getContacts();
    }
  },
  {
    immediate: true,
  }
);

const items = computed(() => {
  if (isLoading.value && !isSaving.value) {
    return [];
  }
  const result = contacts.value
    .filter((item) => {
      if (item.ID && Array.isArray(selectedKeys.value) && !selectedKeys.value.includes(item.ID) && item.INACTIVE) {
        return false;
      }
      return true;
    })
    .map((item) => {
      const newItem = {
        ...item,
        NAME: `${item.LAST_NAME}, ${item.FIRST_NAME}`,
        FULL_NAME: `${item.FIRST_NAME} ${item.LAST_NAME}`,
      };
      return newItem;
    });
  if (companyContact.value) {
    result.push(companyContact.value);
  }
  return result;
});

const columns = computed(() => {
  const result = [];
  result.push({
    header: 'Name',
    field: 'NAME',
    hideOnPhone: false,
    class: 'px-0',
  });
  result.push({ header: 'Title', field: 'TITLE', hideOnPhone: false, class: 'px-0' });
  result.push({ header: 'Department', field: 'DEPARTMENT', hideOnPhone: false, class: 'px-0' });
  result.push({ header: 'Email', field: 'EMAIL', hideOnPhone: false, class: 'px-0' });
  result.push({ header: 'Phone', field: 'PHONE_PRIVATE', hideOnPhone: false, class: 'px-0' });
  result.push({ header: 'Mobile', field: 'PHONE_MOBILE', hideOnPhone: false, class: 'px-0' });
  // result.push({ header: 'Telefax', field: 'FAX_DIRECT', hideOnPhone: false, class: 'px-0' });
  // result.push({ header: 'Telex', field: 'TELEX', hideOnPhone: false, class: 'px-0' });
  result.push({
    header: 'Full Name',
    field: 'FULL_NAME',
    hideOnPhone: false,
    class: 'px-0',
  });
  return result;
});

function createRecipientsToInsert(selectedContactIds: number[]) {
  const result: IHotlistContact[] = [];
  selectedContactIds.forEach((id) => {
    const contactId = id === props.clientId ? null : id;
    const item: IHotlistContact = {
      HCO_COM_TYPE: 1,
      HCO_CONTACT_ID: contactId,
      HCO_HOTLIST_ID: props.hotlistId,
      HCO_ID: 0,
    };
    const isDuplicate = () => {
      if (contactId === null) {
        return false;
      }
      return result.find((i) => i.HCO_CONTACT_ID === contactId) !== undefined;
    };
    if (!isDuplicate()) result.push(item);
  });
  return result;
}

async function onOkClick() {
  const orgValue = [...props.selectedContactIds];
  const newValue = [...selectedKeys.value];
  if (JSON.stringify(orgValue.sort()) !== JSON.stringify(newValue.sort())) {
    const recipients = createRecipientsToInsert(selectedKeys.value);
    isLoading.value = true;
    isSaving.value = true;
    await apiPost(`hotlist/client-recipients/${props.hotlistId}`, recipients);
    isSaving.value = false;
    isLoading.value = false;
    emit('recipients-updated');
    emit('input', false);
  } else {
    emit('input', false);
  }
}

return { __sfc: true,props, portcallStore, emit, isLoading, isSaving, selectedKeys, rowClass, contacts, companyContact, getContacts, modelValue, items, columns, createRecipientsToInsert, onOkClick }
}

})